import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, Users, X, KeyRound } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function Workspaces() {
  const { user } = useAuth();
  const { workspaces, refresh, select } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", brand_name: "" });

  // Assign-client dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignWs, setAssignWs] = useState(null);
  const [assignEmail, setAssignEmail] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Manage-users dialog state
  const [manageOpen, setManageOpen] = useState(false);
  const [manageWs, setManageWs] = useState(null);
  const [manageUsers, setManageUsers] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/workspaces", form);
      toast.success("Workspace created");
      setOpen(false);
      setForm({ name: "", brand_name: "" });
      await refresh();
      select(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete workspace "${w.name}"? This removes all its SKUs and sales data.`)) return;
    await api.delete(`/workspaces/${w.id}`);
    toast.success("Deleted");
    await refresh();
  };

  const startAssign = (w) => {
    setAssignWs(w);
    setAssignEmail("");
    setAssignOpen(true);
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!assignWs) return;
    setAssigning(true);
    try {
      const { data } = await api.post(`/workspaces/${assignWs.id}/assign-user`, { email: assignEmail });
      toast.success(`${data.user.email} assigned to ${data.workspace.name}`);
      setAssignOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to assign");
    } finally {
      setAssigning(false);
    }
  };

  const openManage = async (w) => {
    setManageWs(w);
    setManageOpen(true);
    setManageLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${w.id}/users`);
      setManageUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    } finally {
      setManageLoading(false);
    }
  };

  const removeUser = async (u) => {
    if (!manageWs) return;
    if (!window.confirm(`Remove ${u.email} from ${manageWs.name}?`)) return;
    try {
      await api.delete(`/workspaces/${manageWs.id}/users/${u.id}`);
      setManageUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.email} removed`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const resetUserPassword = async (u) => {
    const pw = window.prompt(`Set a temporary password for ${u.email} (min 6 chars):`);
    if (pw === null) return;
    if (!pw || pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.put(`/users/${u.id}/reset-password`, { new_password: pw });
      toast.success(`Temp password set for ${u.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border p-8 flex items-end justify-between">
        <div>
          <div className="overline text-muted-foreground">Admin</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Client Workspaces</h1>
          <p className="text-sm text-muted-foreground mt-2">{workspaces.length} brands under management</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none" data-testid="add-workspace-btn"><Plus className="h-4 w-4 mr-1" /> New Workspace</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-display">New Client Workspace</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label className="overline">Short Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-2" data-testid="ws-name" /></div>
              <div><Label className="overline">Legal Brand Name</Label><Input required value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="rounded-none mt-2" data-testid="ws-brand" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="rounded-none" data-testid="ws-create-submit">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-skeleton border border-border" data-testid="ws-list">
          {workspaces.map((w) => (
            <div key={w.id} className="p-6 flex flex-col justify-between min-h-[160px]" data-testid={`ws-card-${w.id}`}>
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="overline text-muted-foreground">Client</div>
                    <div className="font-display text-xl font-semibold mt-1">{w.name}</div>
                    <div className="text-sm text-muted-foreground mt-1">{w.brand_name}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => startAssign(w)}
                    title="Assign a client user to this workspace"
                    data-testid={`ws-assign-${w.id}`}
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-between items-center mt-4">
                <Button variant="outline" size="sm" onClick={() => select(w)} className="rounded-none" data-testid={`ws-open-${w.id}`}>Open</Button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openManage(w)}
                    className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid={`ws-manage-${w.id}`}
                  >
                    <Users className="h-3 w-3" /> Manage Users
                  </button>
                  <Button variant="ghost" size="icon" onClick={() => remove(w)} data-testid={`ws-del-${w.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {workspaces.length === 0 && <div className="p-12 text-center text-muted-foreground col-span-full">No workspaces yet.</div>}
        </div>
      </div>

      {/* Assign Client Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="rounded-none" data-testid="assign-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Assign Client</DialogTitle>
            {assignWs && (
              <div className="text-xs text-muted-foreground font-mono mt-1">
                Grants access to <span className="text-foreground">{assignWs.name}</span> · {assignWs.brand_name}
              </div>
            )}
          </DialogHeader>
          <form onSubmit={submitAssign} className="space-y-4">
            <div>
              <Label className="overline">Client Email</Label>
              <Input
                required
                type="email"
                value={assignEmail}
                onChange={(e) => setAssignEmail(e.target.value)}
                placeholder="client@brand.com"
                className="rounded-none mt-2"
                data-testid="assign-client-email"
              />
              <p className="text-[11px] text-muted-foreground mt-2">
                The user must already have an account. Their role will be set to <span className="font-mono">client</span> and scoped to this workspace only.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={assigning} className="rounded-none" data-testid="assign-client-submit">
                {assigning ? "Assigning…" : "Assign"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Users Dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="manage-users-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Manage Users</DialogTitle>
            {manageWs && (
              <div className="text-xs text-muted-foreground font-mono mt-1">
                <span className="text-foreground">{manageWs.name}</span> · {manageWs.brand_name}
              </div>
            )}
          </DialogHeader>
          <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="manage-users-list">
            {manageLoading && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}
            {!manageLoading && manageUsers.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border">
                No users assigned yet. Use the <span className="font-mono">+</span> icon to assign one.
              </div>
            )}
            {!manageLoading && manageUsers.map((u) => (
              <div
                key={u.id}
                className="border border-border px-3 py-2 flex items-center justify-between"
                data-testid={`manage-user-row-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-mono truncate">{u.email}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{u.role}{u.name ? ` · ${u.name}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resetUserPassword(u)}
                    className="rounded-none text-xs"
                    data-testid={`manage-user-reset-${u.id}`}
                  >
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset Password
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUser(u)}
                    className="rounded-none text-[hsl(var(--negative))] hover:bg-[hsl(var(--negative))]/10"
                    data-testid={`manage-user-remove-${u.id}`}
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setManageOpen(false)} className="rounded-none">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
